import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/GridLegacy';
import ReactMarkdown from 'react-markdown';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { GetJONGroup } from '../../../data/apiCalls';
import { TOP_LEVEL_ROUTES } from '../../../scripts/routes';
import PageMobileTitle from '../../shared/Pages/PageMobileTitle';
import { mapAndTransformSpecialtyString } from '../../../scripts/JONGroupPageHelpers';
import PageBanner from '../../shared/Pages/PageBanner';
import PageBreadcrumbs from '../../shared/Pages/PageBreadcrumbs';
import PageNotFound from '../../Errors/PageNotFound';
import PageFailure from '../../Errors/PageFailure';
import useIsMobile from '../../../hooks/useIsMobile';
import MarkdownLinkRenderer from '../../shared/MarkdownLinkRenderer';
import MarkdownParagraphRenderer from '../../shared/MarkdownParagraphRenderer';
import MarkdownUnorderListRenderer from '../../shared/MarkdownUnorderedListRenderer';
import MarkdownListItemRenderer from '../../shared/MarkdownListItemRenderer';
import JONGroupPageSkeletonLoading from './JONGroupPageSkeleton';

const JONGroupPage = () => {
	const { groupPageId: pageId } = useParams();
	const groupPageHeader = 'Join Our Network';
	const isMobile = useIsMobile();
	const { transformedString } = mapAndTransformSpecialtyString(pageId);

	const {
		data: response,
		error,
		isLoading,
		isError,
	} = useQuery(['pageId', pageId], () => GetJONGroup(pageId), {
		staleTime: Infinity,
	});

	const status = response?.status || error?.response?.status;
	const {
		data: { resultCode, subResultCode, data: { result = {} } = {} } = {},
	} = response || {};

	const hasError =
		isError || (status >= 400 && status <= 505) || resultCode !== 0;
	const error404 = status === 404 || subResultCode === 9001;

	const crumbs = [
		{
			text: 'Join Our Network - Providers',
			link: `/${TOP_LEVEL_ROUTES.joinOurNetwork}/providers`,
		},
		{ text: transformedString, link: `/resources/${pageId}` },
	];

	const renderContent = () => {
		if (error404 || hasError) {
			return error404 ? <PageNotFound /> : <PageFailure />;
		}
		return (
			<Container>
				{isLoading && <JONGroupPageSkeletonLoading />}

				{isMobile ? (
					<PageMobileTitle title={groupPageHeader.text} />
				) : (
					!hasError && <PageBreadcrumbs crumbs={crumbs} />
				)}

				<PageBanner
					error={hasError}
					imageUrl={result?.banner}
					title={groupPageHeader}
					subtitle={result.title}
				/>

				<Box
					sx={{
						mb: { xs: 2, md: 3 },
						mt: { xs: 2, md: 3 },
					}}
				>
					<Typography
						component='h2'
						variant='h4'
						color='primary'
						data-testid='jon-group-page-title'
					>
						{result.title}
					</Typography>
				</Box>
				<Divider sx={{ my: { xs: 2, md: 3 } }} />
				<Grid container mb={{ xs: 4 }}>
					<Grid item xs={12} key={result.pageId}>
						<ReactMarkdown
							components={{
								a: MarkdownLinkRenderer,
								p: MarkdownParagraphRenderer,
								ul: MarkdownUnorderListRenderer,
								li: MarkdownListItemRenderer,
							}}
						>
							{result.body}
						</ReactMarkdown>
					</Grid>
				</Grid>
			</Container>
		);
	};
	return (
		<Container>
			{isLoading ? <JONGroupPageSkeletonLoading /> : renderContent()}
		</Container>
	);
};

export default JONGroupPage;
